import type { FulfillmentMethod } from '@wix/ambassador-restaurants-v1-fulfillment-method/types';

import type {
  DispatchesInfo,
  DispatchInfo,
  DispatchType,
} from '../../../types/businessTypes';

export type HeaderPricing = {
  minOrder?: string;
  fee?: string;
  freeThreshold?: string;
};

type ResolveHeaderPricingParams = {
  fulfillmentMethods: FulfillmentMethod[];
  selectedDispatchType?: DispatchType;
  dispatchesInfo: Partial<DispatchesInfo>;
};

const EMPTY_PRICING: HeaderPricing = {};

/**
 * Returns the value shared by every method, or `undefined` when they disagree.
 *
 * Without an address we cannot tell which delivery area applies, so rendering
 * one area's number would be misleading - we render nothing instead.
 */
const getConsistentValue = (
  values: (string | null | undefined)[],
): string | undefined =>
  values.length === 0 || values.some((value) => value !== values[0])
    ? undefined
    : values[0] ?? undefined;

const getEnabledMethods = (
  fulfillmentMethods: FulfillmentMethod[],
  dispatchType: DispatchType,
): FulfillmentMethod[] =>
  fulfillmentMethods.filter(
    (method) => (method.type as string) === dispatchType && method.enabled,
  );

/**
 * The time slot is fetched for the entered address, so its fulfillment details
 * already belong to the delivery area that matched.
 */
const fromSelectedTimeSlot = (
  dispatchInfo: DispatchInfo,
): HeaderPricing | undefined => {
  if (!dispatchInfo.address || !dispatchInfo.selectedTimeSlot) {
    return undefined;
  }

  const { fee, minOrderPrice, freeFulfillmentPriceThreshold } =
    dispatchInfo.selectedTimeSlot.fulfillmentDetails;

  return {
    minOrder: minOrderPrice ?? undefined,
    fee: fee ?? undefined,
    freeThreshold: freeFulfillmentPriceThreshold ?? undefined,
  };
};

const fromConfiguredMethods = (
  methods: FulfillmentMethod[],
): HeaderPricing => ({
  minOrder: getConsistentValue(methods.map((method) => method.minOrderPrice)),
  fee: getConsistentValue(methods.map((method) => method.fee)),
  freeThreshold: getConsistentValue(
    methods.map((method) => method.deliveryOptions?.freeDeliveryThreshold),
  ),
});

/**
 * Resolves the pricing shown in the header badges for the selected dispatch
 * type: the area matched for the customer's address when we know it, otherwise
 * the configured methods - but only where they all agree.
 */
export const resolveHeaderPricing = ({
  fulfillmentMethods,
  selectedDispatchType,
  dispatchesInfo,
}: ResolveHeaderPricingParams): HeaderPricing => {
  if (!selectedDispatchType) {
    return EMPTY_PRICING;
  }

  const dispatchInfo = dispatchesInfo[selectedDispatchType];
  const resolvedForAddress = dispatchInfo && fromSelectedTimeSlot(dispatchInfo);

  return (
    resolvedForAddress ??
    fromConfiguredMethods(
      getEnabledMethods(fulfillmentMethods, selectedDispatchType),
    )
  );
};
